import {
  AccessLevel,
  Box,
  H2,
  H3,
  ListDriveInfo,
  ModalContainer,
  PublicSampleList,
  SampleRecordings,
} from '@ouster/webviz-react-components';

import { useCallback, useMemo, useState } from 'react';
import { PublicDownloadWarning } from 'src/components/publicSample/PublicDownloadWarning';
import { hasUserSelectionOnTable } from 'src/utils/tableUtils';

import demoStyles from './inAppDemo.module.css';
import { useFullyPublicListQuery } from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';

export default function PublicCollection() {
  const [isDownloadWarningOpen, setIsDownloadWarningOpen] = useState(false);

  const openRecordingDownloadPopover = useCallback(
    () => setIsDownloadWarningOpen(true),
    [setIsDownloadWarningOpen]
  );

  const { data, isLoading } = useFullyPublicListQuery(
    undefined, // curUserOrg - not needed for public queries
    'updated_at', // orderBy
    'desc', // sortOrder
    0, // pageIndex
    100, // pageSize
    '' // searchParams (no filters)
  );

  const { showToast } = useChakraHook();

  const openVizPage = useCallback(
    (recording: ListDriveInfo) => {
      if (isLoading) return;
      if (hasUserSelectionOnTable()) return;
      if (recording.public_url) {
        const url = recording.public_url;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [isLoading]
  );

  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        showToast({
          title: '',
          description: 'Link copied to clipboard',
          status: 'success',
        });
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    },
    [showToast]
  );

  const copyCloudPublicLink = useCallback(
    (recording: ListDriveInfo) => {
      if (recording.public_url) {
        copyToClipboard(recording.public_url);
      }
    },
    [copyToClipboard]
  );

  const featuredSampleData = useMemo(
    () =>
      data?.filter(
        (recording) =>
          recording.publication_access_level ===
          AccessLevel.PUBLIC_SAMPLE_FEATURED
      ),
    [data]
  );

  const nonFeaturedSampleData = useMemo(
    () =>
      data?.filter(
        (recording) =>
          recording.publication_access_level === AccessLevel.PUBLIC_SAMPLE
      ),
    [data]
  );

  return (
    <Box
      className={`${demoStyles['page-container-sample']}`}
      pr="36px"
      pb="36px"
      data-testid="public-collection-root"
    >
      <Box display="grid" rowGap={4}>
        <H2 fontWeight="medium" fontSize={30}>
          Visualize Lidar Data with Ouster Studio
        </H2>
        <Box as="p" lineHeight={1.8} fontSize={16} maxW="96ch">
          Welcome to the public collection of Ouster lidar datasets. Discover
          diverse, real-world data and experience the powerful visualization
          capabilities of Ouster Studio. This is the perfect way to get started
          with lidar.
        </Box>
      </Box>

      <Box
        display="grid"
        rowGap={4}
        alignContent="start"
        data-testid="public-collection-featured-section"
      >
        <H3 fontSize={20} fontWeight="medium">
          Featured Samples: See Ouster Studio in Action
        </H3>
        <SampleRecordings
          featureRecordingDownloadHandler={openRecordingDownloadPopover}
          sampleData={featuredSampleData}
          copyCloudPublicLink={copyCloudPublicLink}
          openVizPage={openVizPage}
          isLoading={isLoading}
        />
      </Box>

      <PublicSampleList
        featureRecordingDownloadHandler={openRecordingDownloadPopover}
        sampleData={nonFeaturedSampleData}
        copyCloudPublicLink={copyCloudPublicLink}
        openVizPage={openVizPage}
        isLoading={isLoading}
        data-testid="public-collection-sample-list"
      />

      {isDownloadWarningOpen && (
        <ModalContainer
          isOpen={isDownloadWarningOpen}
          onClose={() => setIsDownloadWarningOpen(false)}
          header="Unlock Sample lidar Downloads"
          footer={null}
          content={<PublicDownloadWarning />}
          data-testid="public-collection-download-warning-modal"
        />
      )}
    </Box>
  );
}
